/** The rows below the data: the stand-in for a row being added, and the blank row to add one. */

import React from 'react';
import type { Column } from '@tanstack/react-table';
import { TextCell } from './cells/TextCell';
import type { CellMove } from './dom';
import type { Pin } from './pinning';
import { SpreadsheetCell } from './SpreadsheetRow';
import type { StandIn } from './useNewRow';

type BelowRowProps<T> = {
  leafColumns: Column<T, unknown>[];
  pins: readonly Pin[];
  /** The cursor's column in this row, or -1. */
  activeCol: number;
  activeCellRef: React.MutableRefObject<HTMLTableCellElement | null>;
};

/** What was typed, drawn as a row until the real one arrives. Takes no input. */
export function StandInRow<T>({
  standIn,
  leafColumns,
  pins,
  activeCol,
  activeCellRef,
}: BelowRowProps<T> & { standIn: StandIn }) {
  return (
    <tr className='bg-white'>
      {leafColumns.map((column, colIndex) => (
        <SpreadsheetCell
          key={column.id}
          pin={pins[colIndex]}
          width={column.getSize()}
          active={activeCol === colIndex}
          activeCellRef={activeCellRef}
        >
          <div className='flex h-9 items-center px-2 text-[13px]'>
            <span className='truncate'>
              {column.id === standIn.columnId ? standIn.value : ''}
            </span>
          </div>
        </SpreadsheetCell>
      ))}
    </tr>
  );
}

/** The blank row: typing into one of `columns` adds a row. */
export function BlankRow<T>({
  columns,
  rowIndex,
  creating,
  refusedColumnId,
  refusedAt,
  onSelectCell,
  onCreate,
  leafColumns,
  pins,
  activeCol,
  activeCellRef,
}: BelowRowProps<T> & {
  columns: readonly string[];
  rowIndex: number;
  /** A row is being added; the blank row waits for it. */
  creating: boolean;
  refusedColumnId: string | null;
  refusedAt: number;
  onSelectCell: (rowIndex: number, colIndex: number) => void;
  onCreate: (columnId: string, value: string, move?: CellMove) => void;
}) {
  return (
    // The tint mixed into white rather than see-through, so pinned cells,
    // which take on the row colour, cover what scrolls under.
    <tr className='bg-[color-mix(in_srgb,hsl(var(--primary))_6%,white)]'>
      {leafColumns.map((column, colIndex) => {
        const header = column.columnDef.header;
        const title = typeof header === 'string' ? header : '';
        return (
          <SpreadsheetCell
            key={column.id}
            pin={pins[colIndex]}
            width={column.getSize()}
            active={activeCol === colIndex}
            activeCellRef={activeCellRef}
            onSelect={() => onSelectCell(rowIndex, colIndex)}
            refusedAt={refusedColumnId === column.id ? refusedAt : undefined}
          >
            {columns.includes(column.id) ? (
              <TextCell
                value=''
                placeholder={colIndex === 0 ? `+ ${title}` : title}
                disabled={creating}
                onCommit={(value, move) => onCreate(column.id, value, move)}
              />
            ) : (
              <div className='h-9' />
            )}
          </SpreadsheetCell>
        );
      })}
    </tr>
  );
}
